import { Component } from '@theme/component';

/**
 * Recharge SCI selling-plan bridge.
 *
 * The Recharge Subscription Widget is a separate (open) Shadow-DOM app block. On Shopify
 * Checkout Integration, a subscription is a native Shopify selling plan, and the cart contract
 * is `{ id, quantity, selling_plan }` -> /cart/add.js. The native theme submit already posts
 * `new FormData(form)`; the only missing token is a `selling_plan` value inside the form.
 *
 * This bridge reads the widget's current state and writes it into a hidden
 * `<input name="selling_plan">` that lives inside the product form (rendered by
 * blocks/buy-buttons.liquid). The Purchase Rail and the native submit path are untouched —
 * the rail puppets the same form, so it inherits the value for free.
 *
 * Fail-safe: the input is only enabled + filled when a subscription is *positively* selected.
 * For one-time, or any ambiguous/unknown state, the input is cleared AND disabled. Disabled
 * controls are excluded from FormData, so a one-time add never carries a selling_plan, and an
 * empty value never reaches /cart/add. The worst-case failure is "subscription not applied"
 * (visible, recoverable) rather than "one-time wrongly subscribed".
 *
 * Re-sync triggers:
 *  - the widget's public `planChange` event (subscription select + frequency change), and
 *  - `change` events / attribute mutations inside the widget's open shadow root, which cover
 *    the One-Time transition (it does not emit planChange) and the initial on-load state.
 *
 * Widget-internal selectors are isolated in the constants below; if a Recharge update changes
 * the widget's internals, this is the only part that needs revisiting.
 */

const WIDGET_TAG = 'recharge-subscription-widget';

// --- Widget adapter: the only Recharge-internal assumptions, kept together on purpose. ---
const SELECTED_OPTION = '.rc-purchase-option--selected';
const READY_SELECTOR = 'input[type="radio"], .rc-purchase-option--selected, rc-selling-plans';
const ONE_TIME_RE = /one[\s_-]?time|onetime|one[\s_-]?off|single/i;
const SUBSCRIPTION_RE = /subscri|deliver|recurring|every|save/i;
// Recharge selling-plan ids are large integers (e.g. 1375764559).
const PLAN_ID_RE = /^\d{6,}$/;
const READY_TIMEOUT_MS = 20000;

/**
 * @typedef {object} BridgeRefs
 * @property {HTMLInputElement} sellingPlanInput - Hidden selling_plan input inside the product form.
 * @extends {Component<BridgeRefs>}
 */
class RechargeSellingPlanBridge extends Component {
  requiredRefs = ['sellingPlanInput'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #widget = null;

  /** @type {MutationObserver | null} */
  #shadowObserver = null;

  /** Last selling-plan id reported by the widget's `planChange` event. @type {string} */
  #lastPlanId = '';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    // `planChange` is dispatched on the document by the widget (verified on document + window).
    document.addEventListener('planChange', this.#onPlanChange, { signal });

    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#shadowObserver?.disconnect();
  }

  /** @param {Event} event */
  #onPlanChange = (event) => {
    const id = /** @type {any} */ (event)?.detail?.sellingPlanId;
    if (id != null && String(id).length) this.#lastPlanId = String(id);
    this.#sync();
  };

  async #init() {
    const widget = await this.#waitForWidget();
    if (!widget || this.#abortController.signal.aborted) return;
    this.#widget = widget;

    const root = widget.shadowRoot;
    if (root) {
      const { signal } = this.#abortController;
      // Delegated change listener for radios / toggles inside the open shadow root.
      root.addEventListener('change', this.#sync, { signal });
      // Class / attribute toggles (e.g. rc-purchase-option--selected) may not emit `change`.
      this.#shadowObserver = new MutationObserver(this.#sync);
      this.#shadowObserver.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'checked', 'aria-checked', 'aria-selected'],
      });
    }

    // Initial state — must be correct before any user interaction (AT#4 / AT#5).
    this.#sync();
  }

  /**
   * Resolves with the widget once it exists and its open shadow root has rendered its options.
   * Resolves with whatever is present (or null) after a timeout so init never hangs.
   * @returns {Promise<HTMLElement | null>}
   */
  #waitForWidget() {
    return new Promise((resolve) => {
      const found = () => {
        const el = /** @type {HTMLElement | null} */ (document.querySelector(WIDGET_TAG));
        return el && el.shadowRoot && this.#widgetReady(el) ? el : null;
      };

      const existing = found();
      if (existing) return resolve(existing);

      const start = performance.now();
      const observer = new MutationObserver(() => {
        const el = found();
        if (el) {
          observer.disconnect();
          resolve(el);
        } else if (performance.now() - start > READY_TIMEOUT_MS) {
          observer.disconnect();
          resolve(/** @type {HTMLElement | null} */ (document.querySelector(WIDGET_TAG)));
        }
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      this.#abortController.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  /** @param {HTMLElement} el */
  #widgetReady(el) {
    return !!el.shadowRoot?.querySelector(READY_SELECTOR);
  }

  /** Read widget state and write it into the hidden input. Fail-safe to one-time. */
  #sync = () => {
    const input = this.refs.sellingPlanInput;
    if (!(input instanceof HTMLInputElement)) return;

    const planId = this.#readSellingPlan();
    if (planId) {
      input.value = planId;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true; // excluded from FormData -> one-time
    }
  };

  /**
   * Active selling-plan id when subscription is positively selected; otherwise '' (one-time or
   * ambiguous -> fail-safe).
   * @returns {string}
   */
  #readSellingPlan() {
    const root = this.#widget?.shadowRoot;
    if (!root) return '';

    if (this.#isOneTimeSelected(root)) return '';
    if (!this.#isSubscriptionSelected(root)) return '';

    // Prefer the most recent planChange id; fall back to reading it from the DOM (e.g. when
    // subscription is the on-load default and no planChange has fired yet).
    return this.#lastPlanId || this.#readActivePlanIdFromDom(root);
  }

  /** @param {ShadowRoot} root */
  #isOneTimeSelected(root) {
    const checked = root.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement && ONE_TIME_RE.test(this.#radioHaystack(checked))) return true;

    const selected = root.querySelector(SELECTED_OPTION);
    const text = selected?.textContent || '';
    return !!selected && ONE_TIME_RE.test(text) && !SUBSCRIPTION_RE.test(text);
  }

  /** @param {ShadowRoot} root */
  #isSubscriptionSelected(root) {
    const checked = root.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement && SUBSCRIPTION_RE.test(this.#radioHaystack(checked))) return true;

    const selected = root.querySelector(SELECTED_OPTION);
    if (selected && SUBSCRIPTION_RE.test(selected.textContent || '')) return true;

    // A planChange has fired and one-time is not the selected option.
    return !!this.#lastPlanId && !this.#isOneTimeSelected(root);
  }

  /** @param {HTMLInputElement} radio */
  #radioHaystack(radio) {
    const label = radio.closest('label')?.textContent || '';
    return `${radio.value} ${radio.id} ${radio.name} ${radio.getAttribute('aria-label') || ''} ${label}`.toLowerCase();
  }

  /**
   * Best-effort read of the selected selling-plan id straight from the shadow DOM, used when no
   * planChange has fired yet (e.g. subscription is the default on load).
   * @param {ShadowRoot} root
   * @returns {string}
   */
  #readActivePlanIdFromDom(root) {
    for (const radio of root.querySelectorAll('input[type="radio"]:checked')) {
      if (radio instanceof HTMLInputElement && PLAN_ID_RE.test(radio.value.trim())) return radio.value.trim();
    }

    const carrier = root.querySelector('[data-selling-plan-id], [data-selling-plan], [data-plan-id]');
    const attr =
      carrier?.getAttribute('data-selling-plan-id') ||
      carrier?.getAttribute('data-selling-plan') ||
      carrier?.getAttribute('data-plan-id') ||
      '';
    return PLAN_ID_RE.test(attr.trim()) ? attr.trim() : '';
  }
}

if (!customElements.get('recharge-selling-plan-bridge')) {
  customElements.define('recharge-selling-plan-bridge', RechargeSellingPlanBridge);
}

/**
 * Diagnostic: widget present, bridge absent.
 *
 * The Recharge widget is its own app block in the product details column, but the bridge element
 * (and the hidden `selling_plan` input it writes to) lives inside blocks/buy-buttons.liquid. Remove
 * buy-buttons from a product template and the widget still renders — a shopper can select a
 * subscription that can NEVER reach the cart, because there is no input to carry the selling plan
 * and no bridge to fill it. Nothing throws, because the component simply never instantiates.
 *
 * This script is loaded on every product page, so the check runs even in the broken state. It polls
 * rather than checking once: the widget mounts asynchronously, and a single early check would miss
 * a slow mount (false negative) while a single late one is wasted on healthy pages. Stops at the
 * first definite answer either way.
 */
(() => {
  const RETRY_MS = 2000;
  const MAX_TICKS = 6;
  let ticks = 0;

  const check = () => {
    // Bridge present — nothing to report, stop polling.
    if (document.querySelector('recharge-selling-plan-bridge')) return;

    // Widget without bridge is the broken state. Report once and stop.
    if (document.querySelector(WIDGET_TAG)) {
      console.warn(
        '[recharge-selling-plan-bridge] <%s> is on the page but no <recharge-selling-plan-bridge> is. ' +
          'The bridge and its hidden selling_plan input live in the "Buy buttons" block — it is missing ' +
          'from this product template, so a selected subscription cannot reach the cart.',
        WIDGET_TAG
      );
      return;
    }

    // Neither present yet: the widget may still be mounting. Give it a bounded number of retries.
    if (++ticks < MAX_TICKS) setTimeout(check, RETRY_MS);
  };

  setTimeout(check, RETRY_MS);
})();
